import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import VendorMonogram from '../components/VendorMonogram';
import { counterpartyPath, fetchCounterparty } from '../features/counterparties/counterpartyApi';
import {
  CounterpartyActivity,
  CounterpartyContracts,
  CounterpartyIdentity,
  CounterpartyRequests,
  CounterpartyStats,
  CounterpartyUpcoming,
} from '../features/counterparties/CounterpartySections';

// Counterparty 360 (doc-174). Read-only aggregation of one relationship. Rows
// deep-link into Contracts / TPRM — nothing is edited here.
export default function CounterpartyDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCounterparty(key)
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        setError('');
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPayload(null);
        setError(err.status === 404 ? 'notfound' : (err.message || 'Failed to load'));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [key, retryToken]);

  // Arriving through the 'n:<name>' form (a ⌘K hit, a contract's counterparty
  // link) resolves server-side; swap the URL for the canonical key so the page
  // is shareable and a group member doesn't keep a subsidiary-shaped URL.
  useEffect(() => {
    const canonical = payload?.entity?.key;
    if (canonical && canonical !== key) {
      navigate(counterpartyPath(canonical), { replace: true });
    }
  }, [payload, key, navigate]);

  if (loading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  if (error === 'notfound') {
    return (
      <div className="canvas-inner animate-fade-in">
        <div className="page-header">
          <div>
            <h1 className="page-title">Counterparty not found</h1>
            <div className="page-subtitle">
              Nothing you can see names this counterparty — it may have been renamed, or every
              contract on it may be restricted.
            </div>
          </div>
        </div>
        <Link to="/counterparties" className="btn outline sm">
          <ArrowLeft className="w-3.5 h-3.5" />
          All counterparties
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="canvas-inner animate-fade-in">
        <div className="callout red">
          <div className="callout-body">
            <strong>Error loading this counterparty.</strong> {error}
            <div style={{ marginTop: 10 }}>
              <button className="btn outline sm" onClick={() => setRetryToken((t) => t + 1)}>
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { entity, totals, contracts, requests, upcomingDates, activity } = payload;

  return (
    <div className="canvas-inner animate-fade-in">
      <Link
        to="/counterparties"
        className="muted row"
        style={{ gap: 4, fontSize: 12, marginBottom: 12, textDecoration: 'none' }}
      >
        <ArrowLeft className="w-3 h-3" />
        All counterparties
      </Link>

      <div className="page-header">
        <div className="row" style={{ gap: 14, minWidth: 0, alignItems: 'flex-start' }}>
          <VendorMonogram name={entity.displayName} size={44} />
          <div style={{ minWidth: 0 }}>
            <h1 className="page-title" style={{ marginBottom: 6 }}>{entity.displayName}</h1>
            <CounterpartyIdentity entity={entity} />
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <Link
            to={`/contracts?search=${encodeURIComponent(entity.displayName)}`}
            className="btn outline sm"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Search contracts
          </Link>
          <button className="btn outline sm" onClick={() => setRetryToken((t) => t + 1)}>
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <CounterpartyStats totals={totals} />

      <div className="detail-grid">
        <div style={{ minWidth: 0 }}>
          <CounterpartyContracts contracts={contracts} displayName={entity.displayName} />
          <CounterpartyRequests requests={requests} />
        </div>
        <div className="detail-rail">
          <CounterpartyUpcoming items={upcomingDates} total={totals.upcomingDates} />
          <CounterpartyActivity events={activity} />
        </div>
      </div>
    </div>
  );
}
